import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, DollarSign, Users, Zap, Shield, Target, CheckCircle, TrendingUp, Clock, Eye, Mail, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ForWholesalersPage() {
  const benefits = [
    {
      icon: Users,
      title: 'Reach Serious Buyers',
      description: 'Your deals go directly to verified investors actively looking to buy. No tire kickers, no time wasters — just qualified buyers ready to close.'
    },
    {
      icon: Zap,
      title: 'List Deals in Minutes',
      description: 'Our streamlined posting process gets your deals live fast. Upload photos, add the numbers, and start receiving inquiries immediately.'
    },
    {
      icon: Eye,
      title: 'Maximum Exposure',
      description: 'Your listings appear in search results, map views, and deal alerts. Investors browsing your target market will see your deals first.'
    },
    {
      icon: Mail,
      title: 'Direct Buyer Communication',
      description: 'Receive offers and messages directly through the platform. No middlemen, no fees on your assignments — just direct connections.'
    },
    {
      icon: Bell,
      title: 'Instant Offer Notifications',
      description: 'Get notified the moment an investor submits an offer or asks a question. Never miss a hot lead while the deal is fresh.'
    },
    {
      icon: Shield,
      title: 'Professional Presentation',
      description: 'Your deals are displayed with deal analysis, profit projections, and professional formatting that builds buyer confidence.'
    }
  ];

  const painPoints = [
    'Spending hours marketing deals on Facebook groups with no responses?',
    'Building buyer lists from scratch every time you move to a new market?',
    'Losing deals because you couldn\'t find a buyer fast enough?',
    'Dealing with flaky "investors" who can\'t actually close?'
  ];

  const features = [
    'Unlimited deal postings',
    'Built-in deal calculator',
    'Direct messaging with buyers',
    'Offer management dashboard',
    'Performance analytics',
    'Mobile-friendly listings'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Real estate wholesaler closing deals"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-[#5DADE2]/20 border border-[#5DADE2]/30 text-[#5DADE2] text-sm font-medium px-4 py-2 rounded-full mb-6">
              <DollarSign className="h-4 w-4" />
              For Wholesalers
            </span>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6" data-testid="wholesaler-hero-title">
              Move Your Deals Faster to Buyers Who Actually Close
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Stop chasing cold leads and posting in Facebook groups. NextFlip puts your wholesale deals in front of thousands of active investors looking to buy right now.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-[#4A9BD1] text-white rounded-full px-8 text-lg h-14" data-testid="wholesaler-signup-btn">
                <Link to="/signup">
                  Start Posting Deals
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 text-lg h-14">
                <Link to="/deals">
                  See Active Listings
                </Link>
              </Button>
            </div>

            <p className="text-slate-400 text-sm">
              Free to join. Post your first deal in under 5 minutes.
            </p>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Sound Familiar?
            </h2>
            <div className="space-y-4">
              {painPoints.map((point, i) => (
                <p key={i} className="text-lg text-slate-600 flex items-start gap-3 text-left">
                  <span className="text-red-500 mt-1">✗</span>
                  {point}
                </p>
              ))}
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-2xl font-heading font-semibold text-slate-900 mb-2">
              There's a better way to move deals.
            </p>
            <p className="text-lg text-[#5DADE2] font-medium">
              NextFlip connects you with buyers who are ready to close.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Built for Wholesalers Who Want to Scale
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to market deals, manage buyers, and close faster.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-[#5DADE2]/50 hover:shadow-lg transition-all group">
                <div className="w-14 h-14 bg-gradient-to-br from-[#5DADE2] to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              From Contract to Assignment in 3 Steps
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Get your deals in front of buyers and start receiving offers today.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#5DADE2] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                1
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                Post Your Deal
              </h3>
              <p className="text-slate-400">
                Add property details, photos, and your assignment fee. Our calculator helps you present the numbers clearly.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#5DADE2] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                2
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                Buyers Find You
              </h3>
              <p className="text-slate-400">
                Investors searching your market see your listing. They can analyze the deal and reach out directly.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#5DADE2] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                3
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                Close & Collect
              </h3>
              <p className="text-slate-400">
                Review offers, negotiate terms, and assign the contract. Keep 100% of your assignment fee.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features List */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Everything You Need to Wholesale Smarter
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                NextFlip gives you the tools to run your wholesaling business more efficiently — no expensive software or complicated systems required.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#5DADE2] flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>

              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-[#4A9BD1] text-white rounded-full px-8">
                <Link to="/signup">
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div className="bg-gradient-to-br from-[#5DADE2] to-blue-600 rounded-3xl p-8 text-white">
              <div className="bg-white/10 rounded-2xl p-6 backdrop-blur mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-blue-100 text-sm">Your Deal Stats</span>
                  <TrendingUp className="h-5 w-5 text-blue-100" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-3xl font-bold">12</div>
                    <div className="text-blue-100 text-sm">Active Listings</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">47</div>
                    <div className="text-blue-100 text-sm">Buyer Inquiries</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">8</div>
                    <div className="text-blue-100 text-sm">Offers Received</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">3</div>
                    <div className="text-blue-100 text-sm">Deals Closed</div>
                  </div>
                </div>
              </div>
              <p className="text-blue-100 text-sm text-center">
                Track your performance and optimize your deal flow
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#5DADE2]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Move More Deals?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of wholesalers using NextFlip to connect with serious buyers and close deals faster.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#5DADE2] hover:bg-blue-50 rounded-full px-8 text-lg h-14">
              <Link to="/signup">
                Create Free Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 text-lg h-14">
              <Link to="/post-deal">
                Post a Deal Now
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
